// Printing a conversation: one standalone document (subject, then every message with its header,
// body and attachment names) printed from a hidden frame, so the app chrome never reaches paper.

import { escapeHtml, formatBytes } from './format.js'
import { ownOrigin } from './mail-html.js'
import { fullDate } from './time.js'

// Remote loads stay blocked on paper too; the images a message carries are `/f/` links on our origin.
const csp = (origin) => `default-src 'none'; img-src ${['data:', origin].filter(Boolean).join(' ')}; style-src 'unsafe-inline'; font-src data:`

const STYLE = `
  @page { margin: 16mm; }
  body { margin: 0; color: #111; font: 12pt/1.45 -apple-system, system-ui, 'Segoe UI', Roboto, sans-serif; }
  h1 { margin: 0 0 4pt; font-size: 17pt; line-height: 1.25; }
  .count { margin: 0 0 14pt; color: #555; font-size: 10pt; }
  .msg { padding: 12pt 0; border-top: 1px solid #bbb; break-inside: auto; }
  .head { display: flex; justify-content: space-between; gap: 12pt; margin-bottom: 2pt; }
  .from { font-weight: 600; }
  .when { flex: none; color: #555; font-size: 10pt; }
  .meta { color: #555; font-size: 10pt; }
  .body { margin-top: 10pt; overflow-wrap: anywhere; }
  .body img { max-width: 100%; height: auto; }
  .files { margin-top: 10pt; color: #555; font-size: 10pt; }
`

const addresses = (list) => (list || []).map((item) => (item.name ? `${item.name} <${item.address}>` : item.address)).filter(Boolean).join(', ')

/** The print document for `subject` and `messages` (full email views, their `body_html` as the reader's View shows it). */
export function printDocument(subject, messages, { origin = ownOrigin() } = {}) {
  const parts = messages.map((email) => {
    const from = email.from_name ? `${email.from_name} <${email.from_addr || ''}>` : email.from_addr || 'Unknown sender'
    const to = addresses(email.to) || email.address
    const cc = addresses(email.cc)
    const body = email.body_html || ''
    const files = (email.files || []).filter((file) => !file.url)
    const fileLine = files.length ? `<div class="files">Attachments: ${files.map((file) => `${escapeHtml(file.filename || 'attachment')} (${formatBytes(file.size)})`).join(', ')}</div>` : ''
    return `<section class="msg">
      <div class="head"><span class="from">${escapeHtml(from)}</span><span class="when">${escapeHtml(fullDate(email.received_at))}</span></div>
      <div class="meta">To: ${escapeHtml(to)}${cc ? `<br>Cc: ${escapeHtml(cc)}` : ''}</div>
      <div class="body">${body}</div>${fileLine}
    </section>`
  })
  const count = messages.length > 1 ? `<p class="count">${messages.length} messages</p>` : ''
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp(origin)}"><title>${escapeHtml(subject)}</title><style>${STYLE}</style></head><body><h1>${escapeHtml(subject)}</h1>${count}${parts.join('')}</body></html>`
}

/**
 * Prints a document from a hidden frame. The frame runs no scripts (sandbox without allow-scripts);
 * allow-same-origin lets this page call print() on it, allow-modals lets the print dialog open.
 */
export function printHtml(html) {
  const frame = document.createElement('iframe')
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  frame.addEventListener('load', () => {
    frame.contentWindow.focus()
    frame.contentWindow.print()
    // print() blocks until the dialog closes in most browsers; drop the frame a bit later either way.
    setTimeout(() => frame.remove(), 1000)
  }, { once: true })
  frame.srcdoc = html
  document.body.append(frame)
}
